import React, { useEffect, useState } from 'react'
import { FaCaretRight } from "react-icons/fa";
import { TreeIconWithFallback, RuneIconWithFallback } from '../shared/BuildRunePageIcons';
import ChampionAbilityIconWithFallBack from '../shared/ChampionAbilityIcons';
import { assetUrl, axiosForContent } from '../helper_functions/utilities';
import { formatSkillOrderPositions } from '../helper_functions/utilities';
import BuildItemIconsWithFallBack from '../shared/BuildItemIcons';

const BuildAndTimeline = ({ highlightedPlayer, individualStats, match }) => {

    const [primaryRow1, setPrimaryRow1] = useState([]);
    const [primaryRow2, setPrimaryRow2] = useState([]);
    const [primaryRow3, setPrimaryRow3] = useState([]);
    const [primaryRow4, setPrimaryRow4] = useState([]);
    const [secondaryRow2, setSecondaryRow2] = useState([]);
    const [secondaryRow3, setSecondaryRow3] = useState([]);
    const [secondaryRow4, setSecondaryRow4] = useState([]);
    const [statModRow1, setStatModRow1] = useState([])
    const [statModRow2, setStatModRow2] = useState([])
    const [statModRow3, setStatModRow3] = useState([])

    const perkStyles = individualStats?.perks?.styles
    const primaryStyle = perkStyles?.[0]?.style
    const secondaryStyle = perkStyles?.[1]?.style
    const championSpellAssetId = individualStats?.championId === 14101 || individualStats?.championId === 14102
        ? 141
        : individualStats?.championId

    useEffect(() => {
        async function getRuneRows() {
            if (!primaryStyle || !secondaryStyle) {
                return
            }

            // Fetch file names for primary runes
            const primaryRow1Names = await axiosForContent.get(`/layouts/runes/major/${primaryStyle}/slot_1/layout.json`);
            const primaryRow2Names = await axiosForContent.get(`/layouts/runes/minor/${primaryStyle}/slot_2/layout.json`);
            const primaryRow3Names = await axiosForContent.get(`/layouts/runes/minor/${primaryStyle}/slot_3/layout.json`);
            const primaryRow4Names = await axiosForContent.get(`/layouts/runes/minor/${primaryStyle}/slot_4/layout.json`);

            // Fetch file names for secondary runes (no Row 1 for secondary, Row 1 are keystones)
            const secondaryRow2Names = await axiosForContent.get(`/layouts/runes/minor/${secondaryStyle}/slot_2/layout.json`);
            const secondaryRow3Names = await axiosForContent.get(`/layouts/runes/minor/${secondaryStyle}/slot_3/layout.json`);
            const secondaryRow4Names = await axiosForContent.get(`/layouts/runes/minor/${secondaryStyle}/slot_4/layout.json`);

            // Fetch file names for stat mods
            const statModRow1Names = await axiosForContent.get(`/layouts/runes/stat-shards/slot_1/layout.json`);
            const statModRow2Names = await axiosForContent.get(`/layouts/runes/stat-shards/slot_2/layout.json`);
            const statModRow3Names = await axiosForContent.get(`/layouts/runes/stat-shards/slot_3/layout.json`);


            // Update each state variable individually
            setPrimaryRow1(primaryRow1Names.data);
            setPrimaryRow2(primaryRow2Names.data);
            setPrimaryRow3(primaryRow3Names.data);
            setPrimaryRow4(primaryRow4Names.data);
            setSecondaryRow2(secondaryRow2Names.data);
            setSecondaryRow3(secondaryRow3Names.data);
            setSecondaryRow4(secondaryRow4Names.data);
            setStatModRow1(statModRow1Names.data);
            setStatModRow2(statModRow2Names.data);
            setStatModRow3(statModRow3Names.data);

            // } catch (error){
            //     console.error('Error fetching rune rows:', error);

            // }

        }
        getRuneRows();
    }, [primaryStyle, secondaryStyle])

    const personalBuildPath = individualStats?.wrgg?.buildPath
    const evolutionOrder = individualStats?.wrgg?.evolutionOrder || []

    const timecutOffs = [
        60000,  // 1 minute
        360000, // 6 minutes
        720000, // 12 minutes
        960000, // 16 minutes
        1200000, // 20 minutes
        1500000, // 25 minutes
        1800000, // 30 minutes
        2100000, // 35 minutes
        2400000, // 40 minutes
        2700000,  // 45 minutes
        3000000, // 50 minutes
        9999999, // rest of game all in milliseconds
    ];

    // Temporary structure to collect items
    const tempGroups = {};

    // Initialize tempGroups with empty arrays for each cutoff
    timecutOffs.forEach(cutoff => {
        tempGroups[cutoff] = [];
    });

    // Loop through each object in the array
    personalBuildPath?.forEach(item => {
        // Find the appropriate cutoff for the current timestamp
        for (let i = 0; i < timecutOffs.length; i++) {
            if (item.timestamp <= timecutOffs[i]) {
                tempGroups[timecutOffs[i]].push(item);
                break; // Once added to a group, break out of the loop
            }
        }
    });

    // Create groupedBuild by filtering out empty arrays
    const groupedBuild = Object.values(tempGroups).filter(group => group.length > 0);

    function roundMillisecondsToNearestMinute(milliseconds) {
        const millisecondsPerMinute = 60000; // 60,000 milliseconds in a minute

        // Calculate the number of minutes and round up
        const roundedMinutes = Math.ceil(milliseconds / millisecondsPerMinute);

        // Format the result as "N min"
        return `${roundedMinutes} min`;
    }

    const isRemake = match?.info?.endOfGameResult === "Abort_Unexpected" || individualStats?.gameEndedInEarlySurrender
    const sectionCardClassName = 'rounded-xl border border-zinc-800 bg-black p-3'
    const sectionHeadingClassName = 'flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] italic text-zinc-300'
    const sectionDividerClassName = `h-px w-full rounded-full ${isRemake ? 'bg-zinc-700/70' : individualStats?.win ? 'bg-cyan-300/35' : 'bg-rose-300/35'}`
    const sectionAccentTextClassName = isRemake ? 'text-zinc-200' : individualStats?.win ? 'text-cyan-200/90' : 'text-rose-200/90'
    const buildStepCardClassName = 'flex min-h-[40px] items-center justify-center gap-1 rounded-xl border border-zinc-800 bg-zinc-950/82 px-2 py-2 shadow-[0_12px_24px_-18px_rgba(0,0,0,0.95)]'
    const timeBadgeClassName = 'inline-flex items-center rounded-full border border-zinc-800 bg-zinc-950/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-300'
    const skillGridClassName = 'grid flex-1 grid-rows-4 gap-0.5 rounded-b-lg border border-zinc-800 border-t-0 bg-black p-1 text-xs'
    const skillCellBorderClassName = 'border-zinc-800'
    const selectedSkillCellClassName = 'bg-zinc-800/90 text-zinc-100'
    const abilityLabelClassName = 'absolute bottom-0 right-0 bg-zinc-950/95 px-0.5 text-xs text-white'
    const skillOrderListColumnClassName = 'flex h-full min-h-0 flex-col self-stretch'
    const skillOrderListBodyClassName = 'grid flex-1 grid-cols-4 gap-0.5 rounded-b-lg border border-zinc-800 border-t-0 bg-black p-1 md:grid-cols-1 md:grid-rows-4'
    const skillSubHeaderClassName = 'rounded-t-lg border border-zinc-800 bg-zinc-950/88 px-2 py-1 text-center text-xs font-semibold italic text-zinc-300'

    return (
        <div className="mt-1 rounded-xl border border-zinc-800 bg-black p-3 shadow-[0_18px_36px_-28px_rgba(0,0,0,0.95)]">
            <div className='grid w-full gap-3 md:grid-cols-3'>
                <div className={sectionCardClassName}>
                    <div className='mb-2 flex flex-col items-center justify-center'>
                        <div className={sectionHeadingClassName}>
                            <TreeIconWithFallback width="18" height="18" alt="primary tree icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[0]?.style}.png`)} />
                            <div>Primary</div>
                        </div>
                    </div>
                    <div className='flex flex-col items-center justify-center'>
                        <div className='mb-1 flex gap-1'>
                            {primaryRow1?.map((name, index) => (
                                <RuneIconWithFallback key={name + toString(index)} format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[0]?.selections?.[0]?.perk) ? "" : "grayscale"}`} width="22" height="22" alt="major rune icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[0]?.style}/slot_1/${name}`)} />
                            ))}
                        </div>
                        <div className='mb-1 flex gap-1'>
                            {primaryRow2?.map((name, index) => (
                                <RuneIconWithFallback key={name + toString(index)} format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[0]?.selections?.[1]?.perk) ? "" : "grayscale"}`} width="22" height="22" alt="major rune icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[0]?.style}/slot_2/${name}`)} />
                            ))}
                        </div>
                        <div className='mb-1 flex gap-1'>
                            {primaryRow3?.map((name, index) => (
                                <RuneIconWithFallback key={name + toString(index)} format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[0]?.selections?.[2]?.perk) ? "" : "grayscale"}`} width="22" height="22" alt="major rune icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[0]?.style}/slot_3/${name}`)} />
                            ))}
                        </div>
                        <div className='flex gap-1'>
                            {primaryRow4?.map((name, index) => (
                                <RuneIconWithFallback key={name + toString(index)} format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[0]?.selections?.[3]?.perk) ? "" : "grayscale"}`} width="22" height="22" alt="major rune icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[0]?.style}/slot_4/${name}`)} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className={sectionCardClassName}>
                    <div className='mb-2 flex flex-col items-center justify-center'>
                        <div className={sectionHeadingClassName}>
                            <TreeIconWithFallback width="18" height="18" alt="secondary tree icon" src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[1]?.style}.png`)} />
                            <div>Secondary</div>
                        </div>
                    </div>
                    <div className='flex flex-col items-center justify-center'>
                        <div className='mb-1 flex gap-1'>
                            {secondaryRow2?.map((name, index) => (
                                <RuneIconWithFallback
                                    key={name + toString(index)}
                                    format={`${(parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[0]?.perk) || parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[1]?.perk)) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[1]?.style}/slot_2/${name}`)}
                                />
                            ))}
                        </div>
                        <div className='mb-1 flex gap-1'>
                            {secondaryRow3?.map((name, index) => (
                                <RuneIconWithFallback
                                    key={name + toString(index)}
                                    format={`${(parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[0]?.perk) || parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[1]?.perk)) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[1]?.style}/slot_3/${name}`)}
                                />
                            ))}
                        </div>
                        <div className='flex gap-1'>
                            {secondaryRow4?.map((name, index) => (
                                <RuneIconWithFallback
                                    key={name + toString(index)}
                                    format={`${(parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[0]?.perk) || parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.styles?.[1]?.selections?.[1]?.perk)) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/trees/${individualStats?.perks?.styles?.[1]?.style}/slot_4/${name}`)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className={sectionCardClassName}>
                    <div className='mb-2 flex flex-col items-center justify-center'>
                        <div className={sectionHeadingClassName}>
                            <div>Stat Mods</div>
                        </div>
                    </div>
                    <div className='flex flex-col items-center justify-center'>
                        <div className='mb-1 flex gap-1'>
                            {statModRow1?.map((name, i) => (
                                <RuneIconWithFallback
                                    key={i}
                                    format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.statPerks?.offense) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/stat_mods/slot_1/${name}`)}
                                />
                            ))}
                        </div>
                        <div className='mb-1 flex gap-1'>
                            {statModRow2?.map((name, i) => (
                                <RuneIconWithFallback
                                    key={i}
                                    format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.statPerks?.flex) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/stat_mods/slot_2/${name}`)}
                                />
                            ))}
                        </div>
                        <div className='flex gap-1'>
                            {statModRow3?.map((name, i) => (
                                <RuneIconWithFallback
                                    key={i}
                                    format={`${parseInt(name?.replace(".png", "")) === parseInt(individualStats?.perks?.statPerks?.defense) ? "" : "grayscale"}`}
                                    width="22"
                                    height="22"
                                    alt="major rune icon"
                                    src={assetUrl(`/major_and_minor_rune_icons/stat_mods/slot_3/${name}`)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='my-3'>
                <div className={sectionDividerClassName}></div>
            </div>

            <div className={sectionCardClassName}>
                <div className='mb-3 flex justify-center'>
                    <div className={sectionHeadingClassName}>Item Build Path</div>
                </div>
                <div className='flex flex-wrap items-center justify-center gap-2'>
                    {groupedBuild?.map((group, index) => (
                        <React.Fragment key={index}>
                            <div className='flex flex-col items-center gap-1.5'>
                                <div className={buildStepCardClassName}>
                                    {group.map((item, itemIndex) => (
                                        <BuildItemIconsWithFallBack key={itemIndex} className='flex rounded-md' height="22" width="22" src={assetUrl(`/item_icons/${item?.itemId}.png`)} />
                                    ))}
                                </div>
                                <div className={timeBadgeClassName}>{roundMillisecondsToNearestMinute(group?.slice(-1)?.[0]?.timestamp)}</div>
                            </div>
                            {index !== (groupedBuild.length - 1) ? (
                            <div className='flex h-[40px] items-center justify-center px-1 text-zinc-300'>
                                    <FaCaretRight className='h-4 w-4' />
                                </div>
                            ) : null}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className='my-3'>
                <div className={sectionDividerClassName}></div>
            </div>

            {evolutionOrder.length ? (
                <div className={`${sectionCardClassName} mb-3 flex flex-col items-center`}>
                    <div className={`${sectionHeadingClassName} mb-2`}>Evolution order</div>
                    <div className='flex flex-wrap items-center justify-center gap-1.5'>
                        {evolutionOrder.map((skill, index) => (
                            <React.Fragment key={`${skill}-${index}`}>
                                {index ? <FaCaretRight className='h-4 w-4 text-zinc-300' /> : null}
                                <div className={buildStepCardClassName}>
                                    <ChampionAbilityIconWithFallBack
                                        width="24"
                                        height="24"
                                        alt={`${skill.toUpperCase()} evolution`}
                                        src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/${skill}.png`)}
                                    />
                                    <span className='text-[11px] font-semibold uppercase tracking-[0.05em] text-zinc-300'>{skill}</span>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className='w-full'>
                <div className='grid gap-3 md:grid-cols-[148px_minmax(0,1fr)] md:items-stretch'>
                    <div className={skillOrderListColumnClassName}>
                        <div className={skillSubHeaderClassName}>Skill Order</div>
                        <div className={skillOrderListBodyClassName}>
                            {individualStats?.wrgg?.skillOrder?.slice(0, 4).map((ability, index) => (
                                <div key={index} className='flex h-full flex-col items-center justify-center gap-1 px-1 md:flex-row md:justify-start md:gap-2 md:px-2'>
                                    <div className={`text-xs md:w-4 md:text-right ${sectionAccentTextClassName}`}>
                                        {formatSkillOrderPositions(index)}
                                    </div>
                                    <ChampionAbilityIconWithFallBack width="24" height="24" alt="champion ability icon" src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/${ability?.skillSlot}.png`)} />
                                    <div className='whitespace-nowrap text-[11px] uppercase tracking-[0.04em] text-zinc-300'>
                                        {ability?.skillSlot?.toUpperCase()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='flex h-full min-h-0 flex-col self-stretch'>
                        <div className={skillSubHeaderClassName}>Per Level</div>
                        <div className={skillGridClassName}>
                            <div className="grid grid-cols-[auto_repeat(18,_1fr)] gap-0.5">
                                <div className="relative">
                                    <ChampionAbilityIconWithFallBack format={`rounded-none border ${skillCellBorderClassName}`} width="20" height="20" alt="champion ability icon" src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/q.png`)} />
                                    <div className={abilityLabelClassName}>Q</div>
                                </div>
                                {Array.from({ length: 18 }).map((_, index) => {
                                    const skill = individualStats?.wrgg?.skillOrder?.[index];
                                    return (
                                        <div key={index} className={`flex h-full items-center justify-center font-semibold border ${skillCellBorderClassName} ${skill?.skillSlot === 'q' ? selectedSkillCellClassName : ''}`}>
                                            {skill?.skillSlot === 'q' ? index + 1 : ""}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-[auto_repeat(18,_1fr)] gap-0.5">
                                <div className="relative">
                                    <ChampionAbilityIconWithFallBack format={`rounded-none border ${skillCellBorderClassName}`} width="20" height="20" alt="champion ability icon" src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/w.png`)} />
                                    <div className={abilityLabelClassName}>W</div>
                                </div>
                                {Array.from({ length: 18 }).map((_, index) => {
                                    const skill = individualStats?.wrgg?.skillOrder?.[index];
                                    return (
                                        <div key={index} className={`flex h-full items-center justify-center font-semibold border ${skillCellBorderClassName} ${skill?.skillSlot === 'w' ? selectedSkillCellClassName : ''}`}>
                                            {skill?.skillSlot === 'w' ? index + 1 : ""}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-[auto_repeat(18,_1fr)] gap-0.5">
                                <div className="relative">
                                    <ChampionAbilityIconWithFallBack format={`rounded-none border ${skillCellBorderClassName}`} width="20" height="20" alt="champion ability icon" src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/e.png`)} />
                                    <div className={abilityLabelClassName}>E</div>
                                </div>
                                {Array.from({ length: 18 }).map((_, index) => {
                                    const skill = individualStats?.wrgg?.skillOrder?.[index];
                                    return (
                                        <div key={index} className={`flex h-full items-center justify-center font-semibold border ${skillCellBorderClassName} ${skill?.skillSlot === 'e' ? selectedSkillCellClassName : ''}`}>
                                            {skill?.skillSlot === 'e' ? index + 1 : ""}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-[auto_repeat(18,_1fr)] gap-0.5">
                                <div className="relative">
                                    <ChampionAbilityIconWithFallBack format={`rounded-none border ${skillCellBorderClassName}`} width="20" height="20" alt="champion ability icon" src={assetUrl(`/champion_spell_icons/${championSpellAssetId}/r.png`)} />
                                    <div className={abilityLabelClassName}>R</div>
                                </div>
                                {Array.from({ length: 18 }).map((_, index) => {
                                    const skill = individualStats?.wrgg?.skillOrder?.[index];
                                    return (
                                        <div key={index} className={`flex h-full items-center justify-center font-semibold border ${skillCellBorderClassName} ${skill?.skillSlot === 'r' ? selectedSkillCellClassName : ''}`}>
                                            {skill?.skillSlot === 'r' ? index + 1 : ""}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BuildAndTimeline
